import { Fragment, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { WastePercentReport } from "../../types/waste-percent-report.type";
import { STICKY_HEADER_Z_INDEX } from "../SaleDepositReport/tableLayout";
import WastePercentLineBlock from "./WastePercentLineBlock";
import {
  TOTAL_ALL_LABEL,
  TOTAL_EXCLUDING_DRY_GOODS_CAPTION,
  TOTAL_EXCLUDING_DRY_GOODS_LABEL,
  UNCATEGORIZED_LABEL,
} from "./constants";
import {
  categoryColumnSx,
  dayColumnSx,
  lineColumnSx,
  productColumnSx,
  sundayCellSx,
  tableContainerSx,
  totalColumnSx,
} from "./tableLayout";
import { MonthDay, categoryLabel, monthDays } from "./wastePercentReport";

/** สินค้า and รายการ, ahead of the day columns. */
const PINNED_COLUMN_COUNT = 2;
/** The รวม column, after the day columns. */
const TOTAL_COLUMN_COUNT = 1;

const PRODUCT_HEADER_LABEL = "สินค้า";
const LINE_HEADER_LABEL = "รายการ";
const TOTAL_HEADER_LABEL = "รวม";

// The pinned header cells sit above the pinned body cells and the scrolling header alike.
const PINNED_HEADER_SX = { zIndex: STICKY_HEADER_Z_INDEX, bgcolor: "background.paper" } as const;
const PRODUCT_HEADER_SX = { ...productColumnSx, ...PINNED_HEADER_SX } as const;
const LINE_HEADER_SX = { ...lineColumnSx, ...PINNED_HEADER_SX } as const;
const SUNDAY_HEADER_SX = { ...dayColumnSx, ...sundayCellSx } as const;
const HEAD_ROW_SX = { "& th": { fontWeight: 600 } } as const;
const CATEGORY_ROW_SX = { bgcolor: "grey.200", "& td": { fontWeight: 600 } } as const;
const PAPER_SX = { p: 0, overflow: "hidden" } as const;

interface WastePercentTableProps {
  report: WastePercentReport;
}

/**
 * The month as the client's Excel sheet lays it out: a heading row per หมวด, each product's eight
 * lines across the days, a รวม column, then รวมขนมทุกชนิด and รวมขนม (หักขนมแห้ง) at the bottom.
 */
export default function WastePercentTable({ report }: Readonly<WastePercentTableProps>) {
  const days = useMemo(
    () => monthDays(report.month, report.days_in_month),
    [report.month, report.days_in_month],
  );
  const scrollingColumnCount = days.length + TOTAL_COLUMN_COUNT;

  return (
    <Paper sx={PAPER_SX}>
      <TableContainer sx={tableContainerSx}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              <TableCell sx={PRODUCT_HEADER_SX}>{PRODUCT_HEADER_LABEL}</TableCell>
              <TableCell sx={LINE_HEADER_SX}>{LINE_HEADER_LABEL}</TableCell>
              {days.map((day) => (
                <DayHeaderCell key={day.dayNumber} day={day} />
              ))}
              <TableCell align="right" sx={totalColumnSx}>
                {TOTAL_HEADER_LABEL}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.categories.map((category) => (
              <Fragment key={category.category_code ?? UNCATEGORIZED_LABEL}>
                <TableRow sx={CATEGORY_ROW_SX}>
                  <TableCell colSpan={PINNED_COLUMN_COUNT} sx={categoryColumnSx}>
                    {categoryLabel(category)}
                  </TableCell>
                  <TableCell colSpan={scrollingColumnCount} />
                </TableRow>
                {category.products.map((product) => (
                  <WastePercentLineBlock
                    key={product.product_code}
                    title={product.product_name}
                    subtitle={product.product_code}
                    lines={product.lines}
                    days={days}
                    isTotal={false}
                  />
                ))}
              </Fragment>
            ))}
            <WastePercentLineBlock
              title={TOTAL_ALL_LABEL}
              lines={report.total_all}
              days={days}
              isTotal
            />
            <WastePercentLineBlock
              title={TOTAL_EXCLUDING_DRY_GOODS_LABEL}
              subtitle={TOTAL_EXCLUDING_DRY_GOODS_CAPTION}
              lines={report.total_excluding_dry_goods}
              days={days}
              isTotal
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function DayHeaderCell({ day }: Readonly<{ day: MonthDay }>) {
  return (
    <TableCell align="center" sx={day.isSunday ? SUNDAY_HEADER_SX : dayColumnSx}>
      {day.dayNumber}
      <Typography component="div" variant="caption" color="text.secondary">
        {day.weekday}
      </Typography>
    </TableCell>
  );
}
